import { addListener } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { RootState } from "@/app/appStore";
import { Schedule } from "@/shared/api";
import { insightsApi } from "@/shared/api/insights-api";
import { useAppDispatch, useAppSelector, useAppStore } from "@/app/appStore";
import { findCurrentSchedule } from "@/shared/helpers/findCurrentSchedule";
import {
  selectArbitraryFromTime,
  selectArbitraryToTime,
  selectFromTime,
  selectToTime,
} from "@/entities/player";

export function useSchedules({
  queryKey,
}: {
  queryKey:
    | "planogramSchedules"
    | "zoneHistorySchedules"
    | "calibrationSchedules";
}) {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const fromTime = useAppSelector(selectFromTime);
  const toTime = useAppSelector(selectToTime);
  const arbitraryFromTime = useAppSelector(selectArbitraryFromTime);
  const arbitraryToTime = useAppSelector(selectArbitraryToTime);

  const { data } = insightsApi.endpoints[queryKey].useQuery({
    from_datetime:
      fromTime ||
      (arbitraryFromTime
        ? new Date(arbitraryFromTime).toISOString()
        : undefined),
    to_datetime:
      toTime ||
      (arbitraryToTime ? new Date(arbitraryToTime).toISOString() : undefined),
    ordering: "-created_at",
  });

  const [activeSchedule, setActiveSchedule] = useState<Schedule | null>(null);
  const isMounted = useRef(false);

  // Keep active schedule up-to-date with currentTime
  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: (_, state, prevState) => {
          return (
            (state as RootState).player.currentTime !==
            (prevState as RootState).player.currentTime
          );
        },
        effect: (_, listenerApi) => {
          const state = listenerApi.getState() as RootState;
          const currentTime = state.player.currentTime;

          if (!data || !currentTime) return;

          try {
            const currentSchedule = findCurrentSchedule(
              data.results,
              currentTime
            );

            if (currentSchedule && currentSchedule?.id !== activeSchedule?.id) {
              setActiveSchedule(currentSchedule);
            }
          } catch (err) {
            toast.error((err as Error).message, {
              id: "schedule-error",
              duration: 1000 * 10,
            });
            unsubscribe(); // Stop listening to currentTime
          }
        },
      })
    );

    return unsubscribe;
  }, [data, activeSchedule?.id]);

  // Set latest schedule as default
  useEffect(() => {
    if (isMounted.current || !data) return;

    const currentTime = store.getState().player.currentTime || Date.now();

    try {
      const defaultSchedule = findCurrentSchedule(data.results, currentTime);

      if (defaultSchedule) {
        setActiveSchedule(defaultSchedule);
        isMounted.current = true;
      }
    } catch (err) {
      toast.error(`Cannot display schedule: ${(err as Error).message}`, {
        id: "schedule-error",
        duration: 1000 * 10,
      });
    }
  }, [data]);

  return { activeSchedule, schedules: data?.results };
}
